import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileSpreadsheet, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { validateFile } from '../utils';

interface FileUploadPanelProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileClear?: () => void;
  onParse: () => void;
  isParsing: boolean;
  disabled?: boolean;
  error?: string | null;
}

export function FileUploadPanel({
  file,
  onFileSelect,
  onFileClear,
  onParse,
  isParsing,
  disabled = false,
  error,
}: FileUploadPanelProps) {
  const { t } = useTranslation('customer-onboarding');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileChange = useCallback(
    (selectedFile: File | null) => {
      if (!selectedFile) return;

      const validationError = validateFile(selectedFile, t);
      if (validationError) {
        setFileError(validationError);
        return;
      }

      setFileError(null);
      onFileSelect(selectedFile);
    },
    [onFileSelect, t]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    handleFileChange(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setFileError(null);
    onFileClear?.();
  };

  const displayError = error || fileError;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragOver && !disabled
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer hover:border-primary/50'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleInputChange}
          disabled={disabled}
          className="hidden"
        />

        {file ? (
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-green-600" />
            <div>
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <Upload className="mb-4 h-10 w-10 text-muted-foreground" />
            <p className="text-center">
              <span className="font-medium text-primary">{t('upload.dragDrop')}</span>
              {' '}{t('upload.dragDropHint')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('upload.fileTypes')}
            </p>
          </>
        )}
      </div>

      {displayError && (
        <p className="text-sm text-destructive">{displayError}</p>
      )}

      {file && !displayError && (
        <div className="flex justify-end">
          <Button onClick={onParse} disabled={disabled || isParsing}>
            {isParsing ? t('upload.parsing') : t('upload.previewButton')}
          </Button>
        </div>
      )}
    </div>
  );
}
